
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import getCsrfToken from 'bits/csrf_token'
import currentUser from 'bits/current_user'
import { captureException } from 'bits/error_tracker'
import { ww } from 'bits/global'
import { __ } from 'bits/intl'
import { currentUrl, navigateTo } from 'bits/location'
import { PiniaMapState } from 'bits/pinia'
import { Component, Vue } from 'bits/vue'
import { useDarkModeStore } from 'pinia/dark_mode'
import { useWindowSizeStore } from 'pinia/window_size'
import BaseButton from 'vuecomponents/base_button.vue'
import BaseSnackbar from 'vuecomponents/base_snackbar.vue'
import ContactFormTextInput from 'vuecomponents/contact_form_text_input.vue'

// Show current response time -- retrieve from Front app
// For future -- enable screenshot/document/etc. attachments

const CONTACT_REASONS = {
  account: {
    title: __('Account / Login'),
    value: 'Account / Login',
    id: 'account',
  },
  billing: {
    title: __('Billing'),
    value: 'Billing',
    id: 'billing',
  },
  bug: {
    title: __('Bug report'),
    value: 'Bug report',
    id: 'bug',
  },
  feedback: {
    title: __('Feedback'),
    value: 'Feedback',
    id: 'feedback',
  },
  report: {
    title: __('Report a padlet'),
    value: 'Report a padlet',
    id: 'report',
  },
  quotation: {
    title: __('Request a quote'),
    value: 'Request a quote',
    id: 'quotation',
  },
  mapDomain: {
    title: __('Map a domain'),
    value: 'Map a domain',
    id: 'mapDomain',
  },
  others: {
    title: __('Others'),
    value: 'Others',
    id: 'others',
  },
}

const MAX_FILE_SIZE_MB = 18
const MAX_FILES_ALLOWED = 3

enum FormError {
  Unknown = 'Unknown',
  MaxFilesExceeded = 'MaxFilesExceeded',
  FileTooLarge = 'FileTooLarge',
  FileTypeNotAllowed = 'FileTypeNotAllowed',
}

type ContactFormJsonResponse = { redirect_to?: string; error?: FormError }

const FORM_ERROR_MESSAGE: Record<FormError, string> = {
  [FormError.Unknown]: __("We're sorry, the form could not be submitted. Please try again."),
  [FormError.MaxFilesExceeded]: __('Too many files are selected. Please select up to %{numFilesAllowed} files.', {
    numFilesAllowed: MAX_FILES_ALLOWED,
  }),
  [FormError.FileTooLarge]: __(
    'The file(s) you selected are too large. Total file size must not exceed %{fileSize}MB.',
    { fileSize: MAX_FILE_SIZE_MB },
  ),
  [FormError.FileTypeNotAllowed]: __('File type is not supported. Please select image files only.'),
}

@Component({ components: { BaseButton, BaseSnackbar, ContactFormTextInput } })
export default class ContactForm extends Vue {
  @PiniaMapState(useWindowSizeStore) isBiggerThanTabletLandscape
  @PiniaMapState(useWindowSizeStore) isSmallerThanTabletLandscape
  @PiniaMapState(useDarkModeStore) isDarkMode

  message = ww.vueStartingState && ww.vueStartingState.message ? decodeURIComponent(ww.vueStartingState.message) : ''
  selected = ww.vueStartingState && ww.vueStartingState.selected ? decodeURIComponent(ww.vueStartingState.selected) : ''
  showFormGenerator = ww.vueStartingState?.showFormGenerator ?? false
  showMapDomainForm = (ww.vueStartingState?.showMapDomainForm || ww.vueStartingState?.showFormGenerator) ?? false

  navigating = false
  errorMessage = ''
  selectableContactReasons = {}

  formInput = {
    name: {
      id: 'name',
      name: __('Name'),
      value: currentUser.name ? currentUser.name : '',
      valid: !!currentUser.name,
    },
    email: {
      id: 'email',
      name: __('Email'),
      value: currentUser.email ? currentUser.email : '',
      valid: !!currentUser.email,
    },
    padletUrl: {
      id: 'padletUrl',
      name: __('Padlet Link'),
      value: '',
    },
    domain: {
      id: 'domain',
      name: __('I would like to map this domain'),
      value: '',
      valid: false,
    },
    mapPadletUrl: {
      id: 'mapPadletUrl',
      name: __('Map the domain to this padlet'),
      value: '',
    },
    registrarName: {
      id: 'registrarName',
      name: __('I am using this domain name registrar'),
      value: '',
    },
  }

  attachments: {
    filename: string
    mime_type: string
    size: number
    content: string
    encoding?: string
  }[] = []

  attachmentsLoadingPromise: Promise<unknown> | null = null

  $refs!: {
    form: HTMLFormElement
    attachmentsInput: HTMLInputElement
  }

  get formClass(): object {
    return {
      tablet: !this.isBiggerThanTabletLandscape,
      'dark-mode': this.isDarkMode,
    }
  }

  get formMessageLabel(): string {
    if (this.selected === 'Map a domain') return __('Any other question, comment or issue?')
    return __('%{messageLabel} *', { messageLabel: __('What is your question, comment, or issue?') })
  }

  get formRadioGroupLabel(): string {
    return __('%{radioGroupLabel} *', { radioGroupLabel: __('What do you need help with?') })
  }

  get formAttachmentsLabel(): string {
    return __(
      'Please add up to %{numFilesAllowed} additional screenshots to help us understand your issue (maximum total file size is %{fileSize}MB)',
      { numFilesAllowed: MAX_FILES_ALLOWED, fileSize: MAX_FILE_SIZE_MB },
    )
  }

  get submitButtonText(): string {
    return this.navigating ? __('Submitting...') : __('Submit')
  }

  get formGeneratedUrl(): string {
    if (!this.showFormGenerator) {
      return ''
    }

    const generatedUrl = new URL(currentUrl({ withQueryString: false }))

    if (this.selected.length > 0) {
      generatedUrl.searchParams.append('selected', this.selected)
      if (this.selected === 'Map a domain') {
        generatedUrl.searchParams.append('map_domain', 'true')
      }
    }

    if (this.message.length > 0) {
      generatedUrl.searchParams.append('message', this.message)
    }

    return generatedUrl.href
  }

  onAttachmentsPicked(event: { target: HTMLInputElement }): void {
    if (!event.target?.files || event.target?.files?.length === 0) return

    const files = Array.from(event.target.files)
    if (files.length > MAX_FILES_ALLOWED) {
      this.showErrorMessage(FormError.MaxFilesExceeded)
      this.clearAttachments()
      return
    }

    const totalFileSize = files.reduce((sum: number, file: File) => sum + file.size, 0)
    if (totalFileSize > MAX_FILE_SIZE_MB * 1024 * 1024) {
      this.showErrorMessage(FormError.FileTooLarge)
      this.clearAttachments()
      return
    }

    if (files.some((file) => !file.type.startsWith('image/'))) {
      this.showErrorMessage(FormError.FileTypeNotAllowed)
      this.clearAttachments()
      return
    }

    // Clear the attachments array every time new files are picked
    this.attachments = []

    this.attachmentsLoadingPromise = Promise.all(
      files.map((file: File): Promise<void> => {
        return new Promise((resolve) => {
          // File content must be converted to base64 encoded string to send in a
          // JSON request.
          const reader = new FileReader()
          reader.addEventListener('load', (): void => {
            if (typeof reader.result === 'string') {
              const base64Content = reader.result.replace(/^data:[\w/*]+;base64,/, '')
              this.attachments.push({
                filename: file.name,
                mime_type: file.type,
                size: file.size,
                content: base64Content,
                encoding: 'base64',
              })
            }
            resolve()
          })
          reader.readAsDataURL(file)
        })
      }),
    )
  }

  get mapDomainMessage(): string {
    return `I would like to map ${this.formInput.domain.value} to ${this.formInput.mapPadletUrl.value}. I am using ${this.formInput.registrarName.value}. Other comments: ${this.message}`
  }

  async submit(): Promise<void> {
    // Only submit after attachments are loaded
    this.attachmentsLoadingPromise && (await this.attachmentsLoadingPromise)

    const userData = {
      name: this.formInput.name.value,
      email: this.formInput.email.value,
      message: this.selected === 'Map a domain' ? this.mapDomainMessage : this.message,
      selected: this.selected,
      padlet_url: this.formInput.padletUrl.value,
      attachments: this.attachments,
    }

    this.navigating = true
    try {
      const options = { method: HTTPMethod.post, csrfToken: getCsrfToken(), jsonData: userData }
      const response: ContactFormJsonResponse = await fetchJson(`/contact-us/mail`, options)

      if (response.error) {
        this.showErrorMessage(response.error)
      } else if (response.redirect_to) {
        navigateTo(response.redirect_to)
        // block this page from being accessed -- if form not submitted
        this.clear()
      }
    } catch (error) {
      captureException(error)
      this.showErrorMessage(FormError.Unknown)
    } finally {
      this.navigating = false
    }
  }

  clear(): void {
    this.clearAttachments()
    this.$refs.form.reset()
  }

  clearAttachments(): void {
    this.$refs.attachmentsInput.value = ''
    this.attachments = []
  }

  showErrorMessage(error: FormError): void {
    this.errorMessage = FORM_ERROR_MESSAGE[error]
  }

  closeErrorMessage(): void {
    this.errorMessage = ''
  }

  update(payload): void {
    const inputId = payload.id
    this.formInput[inputId] = {
      id: payload.id,
      name: payload.name,
      value: payload.value,
      valid: payload.valid,
    }
  }

  mounted(): void {
    this.selectableContactReasons = Object.entries(CONTACT_REASONS)
      .filter((entry) => (this.showMapDomainForm ? true : entry[0] !== 'mapDomain'))
      .reduce((acc, keyValueTuple) => ({ ...acc, [keyValueTuple[0]]: keyValueTuple[1] }), {}) as Record<
      string,
      { id; title; value }
    >
  }
}
